/**
 * Hospital Modal Component
 *
 * Unified modal for viewing and editing hospital information.
 * Handles both detail view (read-only) and edit form modes.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { InputRef } from 'antd';
import { registerModalForBack } from '@/hooks/useModalBackHandler';
import { AddressSearchModal } from '@/components/organisms';
import { AppButton, AppForm, AppFormLabel, AppModal, AppTextField } from '@/components/atoms';
import { PostalCodeFields } from '@/components/molecules';
import useAppToast from '@/hooks/useAppToast';
import { useAddressLookup } from '@/hooks/useAddressLookup';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import {
  mbConvertKana,
  isValidKatakana,
  normalizeTelephoneOrFaxInput,
  TELEPHONE_FAX_FORMAT_REGEX,
} from '@/utils/japaneseText';
import { useFormChangeGuard, useFormChangeGuardWithoutBlocker } from '@/hooks/useFormChangeGuard';
import { type AddressResult } from '@/hooks/useAddressSearch';
import { Hospital, HospitalFormData } from '@/pages/main/hospital/types';

const TELEPHONE_ERROR_MESSAGE = '電話番号の書式が正しくありません。';
const FAX_ERROR_MESSAGE = 'FAXの書式が正しくありません。';
const POSTAL_CODE1_ERROR_MESSAGE = '郵便番号1は3桁にしてください。';
const POSTAL_CODE2_ERROR_MESSAGE = '郵便番号2は4桁にしてください。';

interface HospitalModalPublicProps {
  hospital: Hospital | null;
  isOpen: boolean;
  isEdit: boolean;
  onClose: () => void;
  onSave?: (data: HospitalFormData) => Promise<void>;
  isNew?: boolean;
  /** Skip the React Router navigation blocker (use when nested inside another guarded form) */
  skipBlocker?: boolean;
  /** When true, browser back closes this modal (with change guard) instead of navigating */
  enableBackClose?: boolean;
}

type ChangeGuardHook = typeof useFormChangeGuard;

interface HospitalModalInternalProps extends Omit<HospitalModalPublicProps, 'skipBlocker'> {
  useChangeGuardHook: ChangeGuardHook;
}

const HospitalModalInternal: React.FC<HospitalModalInternalProps> = ({
  hospital,
  isOpen,
  isEdit,
  onClose,
  onSave,
  isNew = false,
  useChangeGuardHook,
  enableBackClose = false,
}) => {
  const toast = useAppToast();
  const addDoctorButtonRef = useRef<HTMLDivElement>(null);

  const emptyFormData: HospitalFormData = {
    name: '',
    name_kana: '',
    postal_code1: '',
    postal_code2: '',
    prefecture_id: undefined,
    address: '',
    telephone: '',
    fax: '',
    doctors: [{ name: '' }],
  };
  const [formData, setFormData] = useState<HospitalFormData>(emptyFormData);
  const [initialFormData, setInitialFormData] = useState<HospitalFormData>(emptyFormData);
  const [submitting, setSubmitting] = useState(false);
  const [addressLookupOpen, setAddressLookupOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setKanaValue = useCallback((kana: string) => {
    setFormData(prev => ({ ...prev, name_kana: kana }));
  }, []);

  const {
    nameInputProps,
    kanaInputProps,
    maxLength: nameKanaMaxLength,
    syncLastKana,
  } = useJapaneseNameKanaFields({
    nameKanaValue: formData.name_kana || '',
    setNameValue: v => setFormData(prev => ({ ...prev, name: v })),
    setKanaValue,
    onClearNameError: () => setErrors(prev => (prev.name ? { ...prev, name: '' } : prev)),
    onClearKanaError: () => setErrors(prev => (prev.name_kana ? { ...prev, name_kana: '' } : prev)),
  });

  // Initialize form data when modal opens; keep formData and initialFormData in sync so
  // the change guard does not treat "fetched detail" as a user edit
  useEffect(() => {
    if (isOpen && isEdit) {
      if (hospital && !isNew) {
        const initial: HospitalFormData = {
          name: hospital.name || '',
          name_kana: hospital.name_kana || '',
          postal_code1: hospital.postal_code1 || '',
          postal_code2: hospital.postal_code2 || '',
          prefecture_id: hospital.prefecture_id || undefined,
          address: hospital.address || '',
          telephone: hospital.telephone || '',
          fax: hospital.fax || '',
          doctors:
            hospital.doctors.length > 0
              ? hospital.doctors.map(d => ({ id: d.id, name: d.name }))
              : [{ name: '' }],
        };

        setFormData(initial);
        setInitialFormData(initial);
        syncLastKana(initial.name_kana ?? '');
      } else {
        setFormData(emptyFormData);
        setInitialFormData(emptyFormData);
        syncLastKana('');
      }
    } else if (!isOpen) {
      setFormData(emptyFormData);
      setInitialFormData(emptyFormData);
      setErrors({});
      syncLastKana('');
    }
  }, [isOpen, isEdit, hospital, isNew, syncLastKana]);

  const { confirmIfChanged, clearFormChange } = useChangeGuardHook(formData, initialFormData);

  const handleClose = () => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  };

  // Register with the modal back-handler stack so the browser back button
  // closes this modal (with change guard) instead of navigating away.
  const addressRef = useRef<InputRef | null>(null);
  const addressSearchButtonRef = useRef<HTMLButtonElement | null>(null);
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  useEffect(() => {
    if (!enableBackClose || !isOpen) return;

    let unregister: (() => void) | null = null;

    const doRegister = () => {
      unregister = registerModalForBack(() => {
        handleCloseRef.current();
        // Re-register in case user cancels the discard-changes dialog.
        // If the modal actually closes, the effect cleanup will unregister.
        doRegister();
      });
    };

    doRegister();
    return () => unregister?.();
  }, [isOpen, enableBackClose]);

  const handleInputChange = (field: keyof HospitalFormData, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const DOCTORS_ERROR_MESSAGE = '医師は必須です。';

  const handleDoctorChange = (index: number, value: string) => {
    const newDoctors = formData.doctors.map((d, i) => (i === index ? { ...d, name: value } : d));
    setFormData(prev => ({ ...prev, doctors: newDoctors }));
    setErrors(prev => (prev.doctors ? { ...prev, doctors: '' } : prev));
  };

  const handleAddDoctor = () => {
    const newDoctors = [...formData.doctors, { name: '' }];
    setFormData(prev => ({ ...prev, doctors: newDoctors }));

    setTimeout(() => {
      if (addDoctorButtonRef.current) {
        addDoctorButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 0);
  };

  const handleRemoveDoctor = (index: number) => {
    const newDoctors = formData.doctors.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, doctors: newDoctors }));
  };

  // Handle address selection from lookup modal (or from inline 1-result fill)
  const handleAddressSelect = (address: AddressResult) => {
    setFormData(prev => ({
      ...prev,
      postal_code1: address.postal_code1,
      postal_code2: address.postal_code2,
      address: address.address,
    }));
  };

  const { search: handleAddressLookupClick, isPending: isAddressSearching } = useAddressLookup({
    getValues: () => ({
      pc1: formData.postal_code1 || '',
      pc2: formData.postal_code2 || '',
      address: formData.address || '',
    }),
    applyResult: ({ pc1, pc2, address }) => {
      setFormData(prev => ({ ...prev, postal_code1: pc1, postal_code2: pc2, address }));
      setErrors(prev => ({ ...prev, postal_code1: '', postal_code2: '' }));
    },
    openModal: () => setAddressLookupOpen(true),
    onAfterApply: () => {
      const nativeInput = addressRef.current?.input;
      if (nativeInput) {
        nativeInput.focus();
        nativeInput.setSelectionRange(nativeInput.value.length, nativeInput.value.length);
      }
    },
  });

  const handleSubmit = async () => {
    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = '医療機関名は必須です。';
    }
    if (formData.name_kana?.trim() && !isValidKatakana(formData.name_kana)) {
      newErrors.name_kana = 'カナの書式が正しくありません。';
    }

    const validDoctors = formData.doctors.filter(d => d.name.trim());
    if (validDoctors.length === 0) {
      newErrors.doctors = DOCTORS_ERROR_MESSAGE;
    }

    const pc1 = (formData.postal_code1 ?? '').replace(/\D/g, '');
    const pc2 = (formData.postal_code2 ?? '').replace(/\D/g, '');
    if (pc1.length > 0 && pc1.length !== 3) {
      newErrors.postal_code1 = POSTAL_CODE1_ERROR_MESSAGE;
    }
    if (pc2.length > 0 && pc2.length !== 4) {
      newErrors.postal_code2 = POSTAL_CODE2_ERROR_MESSAGE;
    }

    const tel = formData.telephone?.trim() ?? '';
    const telDigits = (tel.match(/\d/g) ?? []).length;
    if (tel && (!TELEPHONE_FAX_FORMAT_REGEX.test(tel) || telDigits < 7 || telDigits > 13)) {
      newErrors.telephone = TELEPHONE_ERROR_MESSAGE;
    }

    const fax = formData.fax?.trim() ?? '';
    const faxDigits = (fax.match(/\d/g) ?? []).length;
    if (fax && (!TELEPHONE_FAX_FORMAT_REGEX.test(fax) || faxDigits < 7 || faxDigits > 13)) {
      newErrors.fax = FAX_ERROR_MESSAGE;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      setSubmitting(true);

      // Filter out empty doctors
      const submitData: HospitalFormData = {
        ...formData,
        doctors: formData.doctors.filter(d => d.name.trim()),
      };

      if (onSave) {
        await onSave(submitData);
      }
      clearFormChange();
      onClose();
    } catch (err: unknown) {
      console.error('Save failed:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { errors?: Record<string, string[]> } } };
        if (axiosError.response?.data?.errors) {
          const apiErrors: Record<string, string> = {};
          Object.entries(axiosError.response.data.errors).forEach(([key, value]) => {
            apiErrors[key] = Array.isArray(value) ? value[0] : String(value);
          });
          Object.values(apiErrors).forEach(error => {
            toast.error(error);
          });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AppModal
        isOpen={isOpen}
        onClose={handleClose}
        maxWidth="max-w-3xl"
        title={isNew ? '医療機関登録' : '医療機関編集'}
        onOk={handleSubmit}
        okText={'保存'}
        centered
        okButtonProps={{
          loading: submitting,
          disabled: submitting,
          icon: <i className="mr-2 fa fa-paper-plane" />,
        }}
      >
        <div className="space-y-4 text-base">
          <div className="flex items-start gap-4">
            <AppFormLabel label="医療機関名" required className="w-32 text-base" />
            <div className="flex items-start flex-1 gap-4">
              <div className="flex-1">
                <AppForm.Item
                  validateStatus={errors.name ? 'error' : undefined}
                  help={errors.name}
                  style={{ marginBottom: 0 }}
                >
                  <AppTextField
                    value={formData.name}
                    {...nameInputProps}
                    className="w-full px-3 py-2"
                    maxLength={nameKanaMaxLength}
                    placeholder="入力してください"
                  />
                </AppForm.Item>
              </div>
              <div className="flex items-start flex-1 min-w-0 gap-2">
                <span className="text-base whitespace-nowrap">カナ</span>
                <AppForm.Item
                  validateStatus={errors.name_kana ? 'error' : undefined}
                  help={errors.name_kana}
                  style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
                >
                  <AppTextField
                    value={formData.name_kana || ''}
                    {...kanaInputProps}
                    className="w-full px-3 py-2 min-w-48"
                    maxLength={nameKanaMaxLength}
                    placeholder="入力してください"
                  />
                </AppForm.Item>
              </div>
            </div>
          </div>

          {/* Postal Code */}
          <div className="flex items-start gap-4">
            <AppFormLabel label="郵便番号" className="w-32 text-base" />
            <div className="flex flex-col flex-1 gap-0">
              <PostalCodeFields
                postalCode1={formData.postal_code1 || ''}
                postalCode2={formData.postal_code2 || ''}
                onPostalCode1Change={next => {
                  setErrors(prev => (prev.postal_code1 ? { ...prev, postal_code1: '' } : prev));
                  handleInputChange('postal_code1', next);
                }}
                onPostalCode2Change={next => {
                  setErrors(prev => (prev.postal_code2 ? { ...prev, postal_code2: '' } : prev));
                  handleInputChange('postal_code2', next);
                  if (next.length === 4) {
                    queueMicrotask(() => addressSearchButtonRef.current?.focus());
                  }
                }}
                onPostalCode2Autofill={next => {
                  setErrors(prev => (prev.postal_code2 ? { ...prev, postal_code2: '' } : prev));
                  handleInputChange('postal_code2', next);
                }}
                disabled={submitting}
                errorPostalCode1={errors.postal_code1 ? POSTAL_CODE1_ERROR_MESSAGE : undefined}
                errorPostalCode2={errors.postal_code2 ? POSTAL_CODE2_ERROR_MESSAGE : undefined}
                searchSlot={
                  <AppButton
                    ref={addressSearchButtonRef}
                    type="primary"
                    onClick={handleAddressLookupClick}
                    icon={<i className="fa fa-search" />}
                    className="whitespace-nowrap"
                    loading={isAddressSearching}
                    disabled={submitting || isAddressSearching}
                  >
                    住所補助
                  </AppButton>
                }
              />
            </div>
          </div>

          {/* Address */}
          <div className="flex items-start gap-4">
            <AppFormLabel label="住所" className="w-32 text-base" />
            <div className="flex-1">
              <AppTextField
                ref={addressRef}
                value={formData.address || ''}
                onBlur={e => {
                  const raw = e.target.value;
                  const normalized = mbConvertKana(raw, 'SANK');
                  handleInputChange(
                    'address',
                    normalized.length <= 255 ? normalized : normalized.slice(0, 255)
                  );
                }}
                onChange={e => {
                  const raw = e.target.value;
                  handleInputChange('address', raw.length <= 255 ? raw : raw.slice(0, 255));
                }}
                className="w-full px-3 py-2"
                maxLength={255}
                placeholder="入力してください"
              />
            </div>
          </div>

          {/* Telephone */}
          <div className="flex items-start gap-4">
            <AppFormLabel label="電話番号" className="w-32 text-base" />
            <div className="flex-1">
              <AppForm.Item
                validateStatus={errors.telephone ? 'error' : undefined}
                help={errors.telephone}
                style={{ marginBottom: 0 }}
              >
                <AppTextField
                  value={formData.telephone || ''}
                  onChange={e => {
                    setErrors(prev => (prev.telephone ? { ...prev, telephone: '' } : prev));
                    const normalized = normalizeTelephoneOrFaxInput(e.target.value);
                    handleInputChange('telephone', normalized);
                  }}
                  className="w-full px-3 py-2"
                  maxLength={13}
                  placeholder="入力してください"
                />
              </AppForm.Item>
            </div>
          </div>

          {/* FAX */}
          <div className="flex items-start gap-4">
            <AppFormLabel label="FAX" className="w-32 text-base" />
            <div className="flex-1">
              <AppForm.Item
                validateStatus={errors.fax ? 'error' : undefined}
                help={errors.fax}
                style={{ marginBottom: 0 }}
              >
                <AppTextField
                  value={formData.fax || ''}
                  onChange={e => {
                    setErrors(prev => (prev.fax ? { ...prev, fax: '' } : prev));
                    const normalized = normalizeTelephoneOrFaxInput(e.target.value);
                    handleInputChange('fax', normalized);
                  }}
                  className="w-full px-3 py-2"
                  maxLength={13}
                  placeholder="入力してください"
                />
              </AppForm.Item>
            </div>
          </div>

          {/* Doctors */}
          <div className="flex items-start gap-4">
            <AppFormLabel label="医師名" required className="w-32 text-base" />
            <div className="flex-1">
              <AppForm.Item
                validateStatus={errors.doctors ? 'error' : undefined}
                help={errors.doctors}
                style={{ marginBottom: 0 }}
              >
                <>
                  {formData.doctors.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {formData.doctors.map((doctor, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <AppTextField
                            value={doctor.name}
                            onChange={e => handleDoctorChange(index, e.target.value)}
                            className="flex-1 px-3 py-2"
                            maxLength={255}
                            placeholder="入力してください"
                          />
                          <AppButton
                            type="primary"
                            danger
                            onClick={() => handleRemoveDoctor(index)}
                            icon={<i className="fa fa-trash-o" />}
                          >
                            削除
                          </AppButton>
                        </div>
                      ))}
                    </div>
                  )}
                  <div ref={addDoctorButtonRef}>
                    <AppButton
                      type="primary"
                      onClick={handleAddDoctor}
                      icon={<i className="fa fa-plus" />}
                      size="small"
                    >
                      追加
                    </AppButton>
                  </div>
                </>
              </AppForm.Item>
            </div>
          </div>
        </div>
      </AppModal>

      {/* Address Lookup Modal */}
      <AddressSearchModal
        isOpen={addressLookupOpen}
        onClose={() => setAddressLookupOpen(false)}
        onSelect={handleAddressSelect}
        initialPostalCode1={formData.postal_code1 || ''}
        initialPostalCode2={formData.postal_code2 || ''}
        initialAddress={
          formData.postal_code1 || formData.postal_code2 || (formData.address || '').trim()
            ? (formData.address ?? '')
            : ''
        }
        title="住所補助"
      />
    </>
  );
};

/**
 * Public wrapper that picks the correct change-guard hook based on skipBlocker.
 * Two separate component instances avoid violating Rules of Hooks.
 */
const HospitalModal: React.FC<HospitalModalPublicProps> = ({
  skipBlocker = false,
  enableBackClose = false,
  ...rest
}) => {
  // Pass the appropriate guard hook — skipBlocker is static per mount so the hook
  // identity inside HospitalModalInternal stays stable across renders.
  const hook = skipBlocker ? useFormChangeGuardWithoutBlocker : useFormChangeGuard;
  return (
    <HospitalModalInternal {...rest} useChangeGuardHook={hook} enableBackClose={enableBackClose} />
  );
};

export default HospitalModal;
